(() => {
  'use strict';
  window.noahqExperience?.destroy();
  let dispose = () => {};
  function mount() {
  dispose();
  const host = document.querySelector('[data-noahq-slide-root]');
  if (!host) return;
  const controller = new AbortController();
  const signal = controller.signal;
  const observers = [];
  const listen = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal });
  const root = document.documentElement;
  const deck = host.querySelector('.deck');
  const scenes = [...host.querySelectorAll('.scene')];
  const nav = [...host.querySelectorAll('.chapter-nav a')];
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  const previous = host.querySelector('.previous-scene');
  const next = host.querySelector('.next-scene');
  const status = host.querySelector('#scene-status');
  const count = host.querySelector('.scene-count');
  const label = host.querySelector('.scene-label');
  const position = host.querySelector('.scene-position');
  const reverse = host.querySelector('.construction');
  const chrome = [...host.querySelectorAll('.site-header a,.chapter-nav a,.scene-position,.scene-arrows button')];
  const clamp = (n, low, high) => Math.max(low, Math.min(high, n));
  const threshold = 160;
  const gestureGap = 240;
  const partialInputGap = 650;
  const startHash = location.hash || `#${deck.dataset.previewStart || 'hero'}`;
  let current = Math.max(0, scenes.findIndex(scene => `#${scene.id}` === startHash));
  let busy = false, frame = 0, incomingStarted = false;
  const entries = new Map();
  let sum = 0, direction = 0, lastWheel = -Infinity, consumed = false, feedbackTimer = 0;
  let focusAfterMove = false, touch = null;
  let materialReveal = current === 2 ? 1 : 0;
  let chromeFrame = 0, shownLabel = -1;

  deck.classList.add('is-driven');
  function loadScene(index) {
    scenes[index]?.querySelectorAll('img[loading="lazy"]').forEach(image => { image.loading = 'eager'; });
  }
  function meter(value) { root.style.setProperty('--input', reduced.matches ? 0 : value); }
  function clearInput() {
    sum = 0; direction = 0; meter(0);
    clearTimeout(feedbackTimer);
  }
  function updateState() {
    const scene = scenes[current];
    deck.dataset.scene = scene.id;
    deck.dataset.transition = busy ? 'moving' : 'rest';
    previous.disabled = current === 0;
    next.disabled = current === scenes.length - 1;
    paintChrome();
  }
  function sceneTop(index) { return scenes[index === 2 ? 1 : index].offsetTop; }
  function revealMaterial(value) {
    materialReveal = clamp(value, 0, 1);
    reverse.style.visibility = materialReveal > 0 ? 'visible' : 'hidden';
    reverse.style.clipPath = `polygon(0 0,${materialReveal * 118}% 0,${-18 + materialReveal * 118}% 100%,0 100%)`;
    reverse.dataset.reveal = materialReveal.toFixed(4);
  }
  function sceneAt(x, y, rectangles) {
    const index = rectangles.findIndex((rect, i) => i !== 2 && y >= rect.top && y < rect.bottom);
    if (index !== 1) return index < 0 ? current : index;
    const rect = rectangles[1];
    const px = (x - rect.left) / rect.width;
    const py = (y - rect.top) / rect.height;
    // The same diagonal edge drives the image mask and the foreground colour changes.
    return materialReveal > 0 && px <= 1.18 * materialReveal - .18 * py ? 2 : 1;
  }
  function paintChrome() {
    const rectangles = scenes.map(scene => scene.getBoundingClientRect());
    const viewport = deck.getBoundingClientRect();
    const central = sceneAt(viewport.left + viewport.width / 2, viewport.top + viewport.height / 2, rectangles);
    const centralDark = scenes[central].dataset.tone === 'dark';
    root.classList.toggle('on-dark', centralDark);
    root.classList.toggle('hero-current', central === 0);
    document.body.style.backgroundColor = centralDark ? '#242923' : central === 0 ? '#aaa18b' : central === 2 ? '#d4d4ca' : getComputedStyle(root).getPropertyValue('--swatch-bg');
    for (const node of chrome) {
      const box = node.getBoundingClientRect();
      if (!box.width || !box.height) continue;
      const x = box.left + box.width / 2, y = box.top + box.height / 2;
      const index = sceneAt(x, y, rectangles);
      const portraitFooter = index === 0 && viewport.width / viewport.height <= .8 && y - rectangles[0].top > viewport.height * .65;
      const dark = scenes[index].dataset.tone === 'dark' || portraitFooter;
      node.style.setProperty('--chrome-color', dark ? '#f0eadb' : '#171c17');
      node.style.setProperty('--chrome-shadow', dark ? '#10161085' : '#eee9df70');
      if (node === position && shownLabel !== index) {
        shownLabel = index;
        count.textContent = `${String(index + 1).padStart(2, '0')} / 07`;
        label.textContent = scenes[index].dataset.label;
      }
    }
    nav.forEach((link, index) => {
      if (index === central) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
  }
  function queueChrome() {
    if (!chromeFrame) chromeFrame = requestAnimationFrame(() => { chromeFrame = 0; paintChrome(); });
  }
  function activateScene() {
    scenes.forEach((scene, index) => { scene.inert = index !== current; });
    const heading = scenes[current].querySelector('h1,h2');
    status.textContent = `${current + 1} / ${scenes.length} — ${heading.textContent.replace(/\s+/g, ' ').trim()}`;
    if (focusAfterMove) heading.focus({ preventScroll: true });
    focusAfterMove = false;
  }
  function clearEntries() {
    entries.forEach(entry => entry.animations.forEach(animation => animation.cancel()));
    entries.clear();
  }
  function prepareScene(index) {
    loadScene(index);
    entries.get(index)?.animations.forEach(animation => animation.cancel());
    const entry = { animations: [], started: false };
    entries.set(index, entry);
    if (reduced.matches || typeof deck.animate !== 'function' || index === 1 || index === 2) return;
    const scene = scenes[index];
    const animate = (node, frames, duration = 750, delay = 0, easing = 'cubic-bezier(.16,1,.3,1)') => {
      if (!node) return;
      const animation = node.animate(frames, { duration, delay, easing, fill: 'backwards' });
      // Apply the first frame before this scene can enter the viewport, including delays.
      animation.pause();
      animation.currentTime = 0;
      entry.animations.push(animation);
    };
    if (index === 4) {
      animate(scene.querySelector('.section-meta'), [{ opacity: 0 }, { opacity: 1 }], 420);
      scene.querySelectorAll('.more-title span').forEach((node, i) => animate(node, [
        { opacity: 0, clipPath: 'inset(0 0 100% 0)', transform: 'translate3d(0,24px,0)' },
        { opacity: 1, clipPath: 'inset(0)', transform: 'none' }
      ], 700, i * 70));
      const knit = scene.querySelector('.knit .garment-photo');
      const pants = scene.querySelector('.sweatpants .garment-photo');
      animate(knit, [
        { opacity: 0, transform: 'translate3d(-64px,54px,0) rotate(-15deg)' },
        { opacity: 1, transform: getComputedStyle(knit).transform }
      ], 940, 40, 'cubic-bezier(.22,.8,.22,1)');
      animate(pants, [
        { opacity: 0, transform: 'translate3d(48px,-68px,0) rotate(13deg)' },
        { opacity: 1, transform: getComputedStyle(pants).transform }
      ], 980, 190, 'cubic-bezier(.22,.8,.22,1)');
      scene.querySelectorAll('.garment-label').forEach((node, i) => animate(node, [
        { opacity: 0, clipPath: 'inset(0 100% 0 0)' },
        { opacity: 1, clipPath: 'inset(0)' }
      ], 480, 430 + i * 130));
      animate(scene.querySelector('.more-copy'), [
        { opacity: 0, transform: 'translate3d(0,12px,0)' },
        { opacity: 1, transform: 'none' }
      ], 500, 660);
      return;
    }
    scene.querySelectorAll('.reveal').forEach((node, i) => animate(node, [{ opacity: 0, transform: 'translate3d(0,18px,0)' }, { opacity: 1, transform: 'none' }], 700, i * 80));
    if (index === 0) {
      scene.querySelectorAll('.hero-word span').forEach((node, i) => animate(node, [{ opacity: 0, transform: `translate3d(${i ? '6vw' : '-6vw'},0,0)` }, { opacity: 1, transform: 'none' }], 950, i * 70));
      animate(scene.querySelector('.person-stage'), [{ opacity: 0, transform: 'translate3d(0,24px,0)' }, { opacity: 1, transform: 'none' }], 1050);
    }
    if (index === 3) scene.querySelectorAll('.jeans-motion').forEach((node, i) => animate(node, [
      { opacity: 0, transform: 'translate3d(0,28px,0)' },
      { opacity: 1, transform: getComputedStyle(node).transform }
    ], 850, (i % 12) * 25));
    scene.querySelectorAll('.accessory .object-cut').forEach((node, i) => animate(node, [
      { opacity: 0, transform: `translate3d(${i ? 30 : -30}px,22px,0)` }, { opacity: 1, transform: 'none' }
    ], 950, i * 100));
  }
  function enterScene(index) {
    const entry = entries.get(index);
    if (!entry || entry.started) return;
    entry.started = true;
    entry.animations.forEach(animation => animation.play());
  }
  function finishMove() {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    revealMaterial(current === 2 ? 1 : current === 1 ? 0 : materialReveal);
    deck.scrollTop = sceneTop(current);
    busy = false;
    if (!incomingStarted) enterScene(current);
    incomingStarted = false;
    updateState(); activateScene(); clearInput();
  }
  function go(index, source = 'control') {
    const target = clamp(index, 0, scenes.length - 1);
    if (busy || target === current) { clearInput(); return; }
    const surfaceChange = (current === 1 && target === 2) || (current === 2 && target === 1);
    const revealFrom = materialReveal;
    focusAfterMove = source === 'keyboard' || source === 'link' || scenes[current].contains(document.activeElement);
    prepareScene(target);
    scenes[target].inert = false;
    current = target;
    busy = true; incomingStarted = false;
    if (!surfaceChange && (target === 1 || target === 2)) revealMaterial(target === 2 ? 1 : 0);
    clearInput(); resetPortrait(); updateState();
    // Retain the selected scene in the URL without adding a history entry on every wheel step.
    try { history.replaceState(null, '', `#${scenes[current].id}`); } catch { /* Embedded file previews may restrict history. */ }
    if (reduced.matches) { finishMove(); return; }
    const from = deck.scrollTop, start = performance.now(), duration = 900;
    if (surfaceChange) { incomingStarted = true; enterScene(current); }
    function tick(now) {
      const p = clamp((now - start) / duration, 0, 1);
      const eased = p * p * (3 - 2 * p);
      if (surfaceChange) revealMaterial(revealFrom + ((current === 2 ? 1 : 0) - revealFrom) * eased);
      else deck.scrollTop = from + (sceneTop(current) - from) * eased;
      paintChrome();
      if (!incomingStarted && p >= .48) { incomingStarted = true; enterScene(current); }
      if (p < 1) frame = requestAnimationFrame(tick);
      else finishMove();
    }
    frame = requestAnimationFrame(tick);
  }
  function innerCanScroll(element, delta) {
    if (!element || element.scrollHeight <= element.clientHeight + 2) return false;
    return delta > 0 ? element.scrollTop + element.clientHeight < element.scrollHeight - 2 : element.scrollTop > 2;
  }
  function onWheel(event) {
    if (event.target.closest?.('.nq-home-menu')) return;
    if (event.defaultPrevented || !host.contains(event.target)) return;
    if (event.ctrlKey || event.metaKey || Math.abs(event.deltaX) > Math.abs(event.deltaY) || !event.deltaY) return;
    const now = performance.now();
    if (now - lastWheel > gestureGap) consumed = false;
    if (now - lastWheel > partialInputGap) clearInput();
    lastWheel = now;
    const factor = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? deck.clientHeight : 1;
    const delta = event.deltaY * factor;
    const inner = event.target.closest?.('.scene-scroll');
    if (!busy && innerCanScroll(inner, delta)) {
      // Let a short-window scene scroll naturally, and wait for a fresh gesture at its edge.
      consumed = true; clearInput(); return;
    }
    event.preventDefault();
    if (busy) { consumed = true; return; }
    if (consumed) return;
    const sign = Math.sign(delta);
    if (direction && sign !== direction) clearInput();
    direction = sign;
    if ((current === 0 && sign < 0) || (current === scenes.length - 1 && sign > 0)) { clearInput(); return; }
    sum += Math.min(Math.abs(delta), 90);
    meter(clamp(sum / threshold, 0, 1));
    clearTimeout(feedbackTimer);
    feedbackTimer = setTimeout(() => { if (!busy) clearInput(); }, partialInputGap);
    if (sum >= threshold) { consumed = true; go(current + sign, 'wheel'); }
  }
  listen(window, 'wheel', onWheel, { passive: false });
  listen(window, 'keydown', event => {
    const menu = host.querySelector('.nq-home-menu[open]');
    if (menu) {
      if (event.key === 'Escape') { menu.open = false; menu.querySelector('summary').focus(); }
      return;
    }
    if (!host.contains(event.target) && event.target !== document.body && event.target !== document.documentElement) return;
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.target.closest?.('input,textarea,select,[contenteditable=true]')) return;
    const key = event.key;
    if (key === ' ' && event.target.closest?.('a,button')) return;
    const sign = key === 'ArrowDown' || key === 'PageDown' || (key === ' ' && !event.shiftKey) ? 1 : key === 'ArrowUp' || key === 'PageUp' || (key === ' ' && event.shiftKey) ? -1 : 0;
    if (!sign && key !== 'Home' && key !== 'End') return;
    const inner = event.target.closest?.('.scene-scroll') || scenes[current].querySelector('.scene-scroll');
    if (sign && innerCanScroll(inner, sign)) {
      event.preventDefault();
      inner.scrollBy({ top: sign * Math.min(inner.clientHeight * .6, 240), behavior: reduced.matches ? 'instant' : 'smooth' });
      return;
    }
    event.preventDefault();
    go(key === 'Home' ? 0 : key === 'End' ? scenes.length - 1 : current + sign, 'keyboard');
  });
  host.querySelectorAll('a[href^="#"]').forEach(link => listen(link, 'click', event => {
    const index = scenes.findIndex(scene => `#${scene.id}` === link.hash);
    if (index < 0) return;
    event.preventDefault(); go(index, 'link');
  }));
  listen(previous, 'click', () => go(current - 1));
  listen(next, 'click', () => go(current + 1));

  listen(deck, 'touchstart', event => {
    touch = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY, used: false, inner: event.target.closest?.('.scene-scroll'), horizontal: Boolean(event.target.closest?.('[data-colour-rail],.swatches')) } : null;
  }, { passive: true });
  listen(deck, 'touchmove', event => {
    if (!touch || touch.horizontal || event.touches.length !== 1) return;
    const dx = event.touches[0].clientX - touch.x;
    const dy = touch.y - event.touches[0].clientY;
    if (Math.abs(dx) > Math.abs(dy)) return;
    if (!busy && !touch.used && innerCanScroll(touch.inner, dy)) { touch.used = true; return; }
    // Native inner scrolling must finish before a second swipe changes the scene.
    if (touch.used && touch.inner && touch.inner.scrollHeight > touch.inner.clientHeight + 2) return;
    if (Math.abs(dy) < 8) return;
    if (event.cancelable) event.preventDefault();
    if (busy || touch.used) return;
    meter(clamp(Math.abs(dy) / 64, 0, 1));
    if (Math.abs(dy) >= 64) { touch.used = true; go(current + Math.sign(dy), 'touch'); }
  }, { passive: false });
  listen(deck, 'touchend', () => { touch = null; clearInput(); }, { passive: true });
  listen(deck, 'touchcancel', () => { touch = null; clearInput(); }, { passive: true });

  const colourRail = host.querySelector('[data-colour-rail]');
  const originalGroup = colourRail.querySelector('[data-colour-original]');
  const originalColours = [...originalGroup.querySelectorAll('[data-colour]')];
  const colourSwatches = host.querySelector('.swatches');
  const colourButtons = [...host.querySelectorAll('[data-colour]')];
  let colourDrag = null, suppressColourClick = false;
  function scrollColourSurface(surface, left) {
    surface.scrollLeft = clamp(left, 0, Math.max(0, surface.scrollWidth - surface.clientWidth));
  }
  function centreColour(index) {
    const card = originalColours[index];
    if (!card) return;
    scrollColourSurface(colourRail, colourRail.scrollLeft + card.getBoundingClientRect().left - colourRail.getBoundingClientRect().left + (card.offsetWidth - colourRail.clientWidth) / 2);
  }
  function selectColour(index) {
    const card = originalColours[index];
    if (!card) return;
    root.style.setProperty('--swatch-bg', card.dataset.colourGround);
    host.querySelector('.colour-name').textContent = card.dataset.colourName;
    colourButtons.forEach(item => item.setAttribute('aria-pressed', String(Number(item.dataset.colour) === index)));
    paintChrome();
  }
  listen(host, 'click', event => {
    const button = event.target.closest?.('button[data-colour]');
    if (!button) return;
    if (suppressColourClick) { event.preventDefault(); return; }
    const index = Number(button.dataset.colour);
    selectColour(index);
    if (colourSwatches.contains(button)) centreColour(index);
  });
  [colourRail, colourSwatches].forEach(surface => {
    listen(surface, 'wheel', event => {
      if (event.ctrlKey || event.metaKey || surface.scrollWidth <= surface.clientWidth) return;
      const delta = event.shiftKey && !event.deltaX ? event.deltaY : Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (!delta) return;
      event.preventDefault();
      const factor = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? surface.clientWidth : 1;
      scrollColourSurface(surface, surface.scrollLeft + delta * factor);
    }, { passive: false });
    listen(surface, 'keydown', event => {
      if (event.ctrlKey || event.metaKey || event.altKey || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const button = event.target.closest?.('[data-colour]');
      const step = event.key === 'ArrowLeft' ? -1 : 1;
      if (!button && event.key.startsWith('Arrow')) {
        scrollColourSurface(colourRail, colourRail.scrollLeft + step * (originalColours[1].offsetLeft - originalColours[0].offsetLeft));
        return;
      }
      const index = event.key === 'Home' ? 0 : event.key === 'End' ? originalColours.length - 1 : clamp(Number(button.dataset.colour) + step, 0, originalColours.length - 1);
      const target = surface === colourRail ? originalColours[index] : colourSwatches.querySelector(`[data-colour="${index}"]`);
      target.focus({ preventScroll: true });
      selectColour(index); centreColour(index);
      if (surface === colourSwatches) scrollColourSurface(surface, surface.scrollLeft + target.getBoundingClientRect().left - surface.getBoundingClientRect().left + (target.offsetWidth - surface.clientWidth) / 2);
    });
  });
  listen(colourRail, 'pointerdown', event => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const bounds = colourRail.getBoundingClientRect();
    if (event.clientY >= bounds.top + colourRail.clientTop + colourRail.clientHeight || event.clientX >= bounds.left + colourRail.clientLeft + colourRail.clientWidth) return;
    colourDrag = { id: event.pointerId, startX: event.clientX, lastX: event.clientX, moved: false };
  });
  listen(window, 'pointermove', event => {
    if (!colourDrag || event.pointerId !== colourDrag.id) return;
    if (!colourDrag.moved && Math.abs(event.clientX - colourDrag.startX) < 6) return;
    if (!colourDrag.moved) {
      colourDrag.moved = true;
      colourRail.setPointerCapture(event.pointerId);
      colourRail.classList.add('is-dragging');
    }
    event.preventDefault();
    scrollColourSurface(colourRail, colourRail.scrollLeft + colourDrag.lastX - event.clientX);
    colourDrag.lastX = event.clientX;
  }, { passive: false });
  function finishColourDrag(event) {
    if (!colourDrag || event.pointerId !== colourDrag.id) return;
    suppressColourClick = colourDrag.moved;
    colourDrag = null;
    colourRail.classList.remove('is-dragging');
    if (colourRail.hasPointerCapture(event.pointerId)) colourRail.releasePointerCapture(event.pointerId);
    setTimeout(() => { suppressColourClick = false; }, 0);
  }
  listen(window, 'pointerup', finishColourDrag);
  listen(window, 'pointercancel', finishColourDrag);

  // Only move the original portrait as a whole. No face warping or perspective.
  const portrait = host.querySelector('.person');
  let portraitFrame = 0, portraitX = 0, portraitTarget = 0, portraitTime = 0;
  function resetPortrait() {
    if (portraitFrame) cancelAnimationFrame(portraitFrame);
    portraitFrame = 0; portraitX = 0; portraitTarget = 0; portraitTime = 0;
    portrait.style.removeProperty('transform');
  }
  function tickPortrait(now) {
    portraitFrame = 0;
    if (current !== 0 || busy || reduced.matches || document.hidden) { resetPortrait(); return; }
    const dt = portraitTime ? Math.min(48, now - portraitTime) : 16;
    portraitTime = now;
    portraitX += (portraitTarget - portraitX) * (1 - Math.exp(-dt / 140));
    if (Math.abs(portraitX - portraitTarget) < .01) portraitX = portraitTarget;
    if (portraitX === 0) portrait.style.removeProperty('transform');
    else portrait.style.transform = `translate3d(${portraitX.toFixed(3)}px,0,0)`;
    if (portraitX !== portraitTarget) portraitFrame = requestAnimationFrame(tickPortrait);
    else portraitTime = 0;
  }
  function targetPortrait(x) {
    portraitTarget = x;
    if (!portraitFrame && portraitX !== x) portraitFrame = requestAnimationFrame(tickPortrait);
  }
  listen(scenes[0], 'pointermove', event => {
    if (current !== 0 || busy || reduced.matches || !finePointer.matches || event.pointerType === 'touch') return;
    targetPortrait(clamp(event.clientX / deck.clientWidth * 2 - 1, -1, 1) * 10);
  }, { passive: true });
  listen(scenes[0], 'pointerleave', () => targetPortrait(0));
  listen(window, 'blur', () => { resetPortrait(); clearInput(); });

  const garmentPhotos = [...host.querySelectorAll('#more .garment-photo')];
  garmentPhotos.forEach(photo => listen(photo.querySelector('img'), 'load', syncGarmentCaptions));
  function syncGarmentCaptions() {
    garmentPhotos.forEach(photo => {
      const image = photo.querySelector('img');
      const caption = photo.nextElementSibling;
      if (!image.naturalWidth || !image.clientHeight) return;
      const renderedWidth = Math.min(image.clientWidth, image.clientHeight * image.naturalWidth / image.naturalHeight);
      const value = `${Math.round(renderedWidth)}px`;
      if (caption.style.getPropertyValue('--garment-width') !== value) caption.style.setProperty('--garment-width', value);
    });
  }
  function settleEntry() {
    const entry = entries.get(current);
    if (!entry) return;
    entry.started = true;
    entry.animations.forEach(animation => animation.finish());
  }
  let measuredWidth = 0, measuredHeight = 0;
  function resize() {
    const width = deck.clientWidth, height = deck.clientHeight;
    if (width === measuredWidth && height === measuredHeight) return;
    const hadViewport = measuredWidth > 0;
    measuredWidth = width; measuredHeight = height;
    root.style.setProperty('--vh', `${height}px`);
    resetPortrait(); clearInput();
    if (busy) finishMove();
    else { deck.scrollTop = sceneTop(current); paintChrome(); }
    if (hadViewport) settleEntry();
    syncGarmentCaptions();
  }
  if ('ResizeObserver' in window) {
    const deckObserver = new ResizeObserver(resize);
    deckObserver.observe(deck);
    observers.push(deckObserver);
    const captionObserver = new ResizeObserver(syncGarmentCaptions);
    garmentPhotos.forEach(photo => captionObserver.observe(photo));
    observers.push(captionObserver);
  }
  else listen(window, 'resize', resize, { passive: true });
  listen(window, 'hashchange', () => {
    const index = scenes.findIndex(scene => `#${scene.id}` === location.hash);
    if (index >= 0) go(index, 'link');
  });
  listen(deck, 'scroll', queueChrome, { passive: true });
  listen(window, 'pageshow', () => { resize(); deck.scrollTop = sceneTop(current); paintChrome(); });
  listen(window, 'pagehide', () => { resetPortrait(); clearInput(); if (busy) finishMove(); clearEntries(); });
  listen(document, 'visibilitychange', () => {
    if (document.hidden) { resetPortrait(); clearInput(); if (busy) finishMove(); }
  });
  listen(reduced, 'change', () => {
    clearEntries();
    resetPortrait(); clearInput(); if (busy) finishMove();
  });
  listen(finePointer, 'change', resetPortrait);
  prepareScene(current);
  revealMaterial(materialReveal); resize(); updateState(); activateScene();
  Promise.race([
    Promise.all([document.fonts?.ready, portrait.decode?.().catch(() => {})]),
    new Promise(resolve => setTimeout(resolve, 1500))
  ]).then(() => { if (signal.aborted) return; syncGarmentCaptions(); if (!busy) enterScene(current); deck.dataset.ready = 'true'; });
  dispose = () => {
    controller.abort();
    observers.forEach(observer => observer.disconnect());
    colourRail.classList.remove('is-dragging');
    if (frame) cancelAnimationFrame(frame);
    if (chromeFrame) cancelAnimationFrame(chromeFrame);
    resetPortrait(); clearInput(); clearEntries();
  };
  }
  function unload(event) {
    if (event.target.querySelector('[data-noahq-slide-root]')) dispose();
  }
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', unload);
  window.noahqExperience = { destroy() {
    dispose();
    document.removeEventListener('shopify:section:load', mount);
    document.removeEventListener('shopify:section:unload', unload);
  } };
  mount();
})();
